import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { CategoryProducts } from '@/components/catalogue/products/CategoryProducts';
import { SpecFilterGroup } from '@/components/catalogue/products/useFilteredProducts';
import { Button } from '@/components/form/Button';
import { TextInput } from '@/components/form/fields/text/TextInput';
import SiFilter from '@/icons/SiFilter';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { RoleType } from '@/services/authentication/hooks/user';
import { BundleState } from '@/services/bundles/useBundleService';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { useCategories } from '../../services/api/hooks/catalog';
import { ImpersonationBanner } from './ImpersonationBanner';
import CatalogueExplorerFilterContainer from './filters/catalogueExplorer/CatalogueExplorerFilterContainer';
import { productExplorerStrings } from './strings';

interface ProductExplorerProps {}

interface CategoryExplorerProps {
  bundleState?: BundleState;
}

export const CategoryExplorer: FC<CategoryExplorerProps> = ({ bundleState }) => {
  const navigate = useNavigate();

  const [configuration, showLatest, setShowLatest] = useCatalogueService(state => [
    state.configuration,
    state.showLatest,
    state.setShowLatest
  ]);

  const canSwitchApproved = configuration.as !== RoleType.Company && bundleState === BundleState.postApproval;

  const { categoryId } = useParams<{ categoryId?: string }>();
  const [categories] = useCategories();

  const category = (() => {
    if (!categoryId) return categories?.at(0);
    return categories?.find(category => category.id === categoryId);
  })();

  useEffect(() => {
    setSearchQuery('');
    setSpecFilter(undefined);
  }, [category]);

  const [searchQuery, setSearchQuery] = useState<string>('');

  const onShowApprovalClick = async () => {
    setShowLatest(!showLatest);
  };

  const [specFilter, setSpecFilter] = useState<SpecFilterGroup[]>();
  const [manufacturerFilter, setManufacturerFilter] = useState<string[]>();

  const configCatalogueUrlPrefix =
    configuration.for === RoleType.Partner ? `/msps/${configuration.partnerId}/mspCatalogue` : '/configuratorCatalogue';

  const breadcrumbs: NavPage[] = [
    {
      name: 'DaaS Portal Catalogue ',
      to: configCatalogueUrlPrefix
    },
    {
      name: category?.name || 'Home',
      to: category ? `${configCatalogueUrlPrefix}/${category.id}` : configCatalogueUrlPrefix
    }
  ];

  return (
    <div className="flex h-full flex-1 flex-col">
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="space-y-4">
          <div className="flex w-full items-center justify-between">
            <h2 className="text-3xl font-bold tracking-tight">{category?.name}</h2>
            <div className="flex gap-4">
              {canSwitchApproved && (
                <Button onClick={onShowApprovalClick}>
                  <span>
                    {showLatest ? productExplorerStrings.unapprovedPrices : productExplorerStrings.approvedPrices}
                  </span>
                </Button>
              )}
              <ImpersonationBanner />
              {configuration.as !== RoleType.Company && configuration.for !== RoleType.Admin && (
                <Button onClick={() => navigate('filters')}>
                  <SiFilter className="text-xl" />
                  <span>{productExplorerStrings.manageFilters}</span>
                </Button>
              )}
            </div>
          </div>
          <TextInput
            id="filter"
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={productExplorerStrings.searchPlaceholder}
          />
          {category && (
            <CatalogueExplorerFilterContainer
              categoryId={category.id}
              onFilterCallback={filters => {
                setSpecFilter(filters.specs);
                setManufacturerFilter(filters.manufacturers);
              }}
            />
          )}
        </div>
      </BreadcrumbPageHeader>
      <div className="py-6 overflow-auto">
        {category ? (
          <CategoryProducts
            searchQuery={searchQuery}
            specFilter={specFilter}
            manufacturerFilter={manufacturerFilter}
            categoryId={category.id}
          />
        ) : (
          <p>No categories</p>
        )}
      </div>
    </div>
  );
};

export const ProductExplorer: FC<PropsWithChildren<ProductExplorerProps>> = () => {
  return (
    <div className="flex flex-1 overflow-hidden">
      <Outlet />
    </div>
  );
};
